import { Command } from 'cmdk';
import type { LucideIcon } from 'lucide-react';
import { Search } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { Kbd } from './Kbd.js';

export interface CommandItem {
  id: string;
  label: string;
  /** Mono hint on the right (account slug, route, shortcut). */
  hint?: string;
  /** Keyboard shortcut hint rendered as Kbd, e.g. `⌘1`. */
  shortcut?: string;
  icon?: LucideIcon;
  /** Extra search terms. */
  keywords?: string[];
  disabled?: boolean;
  onSelect: () => void;
}

export interface CommandGroup {
  /** Mono uppercase group label: `ACCOUNTS`, `ACTIONS`, `NAVIGATE`. */
  label: string;
  items: CommandItem[];
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groups: CommandGroup[];
  placeholder?: string;
  /** Controlled search text (optional). */
  search?: string;
  onSearchChange?: (value: string) => void;
  emptyMessage?: string;
  /** Bind ⌘K / Ctrl+K globally while mounted. Default true. Respects text-editor focus. */
  bindShortcut?: boolean;
  /** Mono footer hints; defaults to navigation help. */
  footer?: ReactNode;
  /** Loading state text shown above results. */
  loadingMessage?: string;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable || Boolean(target.closest('.cm-editor'));
}

/** cmdk palette styled as an inset well: mono group labels, ⌘K hint, hairline panel, no shadow. */
export function CommandPalette({
  open,
  onOpenChange,
  groups,
  placeholder = 'Search accounts, actions and views',
  search,
  onSearchChange,
  emptyMessage = 'No matches.',
  bindShortcut = true,
  footer,
  loadingMessage,
}: CommandPaletteProps) {
  useEffect(() => {
    if (!bindShortcut) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'k' || e.altKey || e.shiftKey) return;
      // respect text-editor focus (spec section 22): never steal ⌘K from a code editor
      if (!open && isEditableTarget(e.target) && e.target instanceof HTMLElement && e.target.closest('.cm-editor')) return;
      e.preventDefault();
      onOpenChange(!open);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [bindShortcut, open, onOpenChange]);

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label="Command palette"
      loop
      overlayClassName="fixed inset-0 z-[80] bg-black/60"
      contentClassName="fixed left-1/2 top-[15vh] z-[90] w-[min(640px,calc(100vw-32px))] -translate-x-1/2 outline-none"
      className="flex max-h-[min(480px,70vh)] flex-col overflow-hidden rounded-sm border border-line bg-panel text-ink"
    >
      <div className="flex items-center gap-2 border-b border-hair bg-bg px-3">
        <Search size={12} aria-hidden className="shrink-0 text-mute" />
        <Command.Input
          value={search}
          onValueChange={onSearchChange}
          placeholder={placeholder}
          autoFocus
          className="h-10 w-full bg-transparent text-nav text-ink placeholder:text-mute focus:outline-none"
        />
        <Kbd>⌘K</Kbd>
      </div>
      <Command.List className="scrollbar-thin min-h-0 flex-1 overflow-y-auto p-1">
        {loadingMessage ? <Command.Loading>
          <p className="px-2 py-3 font-mono text-2xs uppercase tracking-[0.18em] text-mute">{loadingMessage}</p>
        </Command.Loading> : null}
        <Command.Empty className="px-3 py-6 text-center text-sm text-mute">{emptyMessage}</Command.Empty>
        {groups.map((g) => (
          <Command.Group key={g.label} heading={g.label} className="[&_[cmdk-group-heading]]:label-mono [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-mute">
            {g.items.map((item) => (
              <Command.Item
                key={item.id}
                value={`${g.label} ${item.label} ${item.hint ?? ''}`}
                keywords={item.keywords}
                disabled={item.disabled}
                onSelect={() => {
                  item.onSelect();
                  onOpenChange(false);
                }}
                className={cn(
                  'relative flex cursor-default select-none items-center gap-2.5 rounded-sm px-2 py-1.5 text-nav text-ink',
                  'data-[disabled=true]:opacity-50 data-[selected=true]:bg-card',
                )}
              >
                <span aria-hidden className="absolute inset-y-1 left-0 hidden w-[2px] bg-brand [[data-selected=true]>&]:block" />
                {item.icon ? <item.icon size={14} strokeWidth={1.5} aria-hidden className="shrink-0 text-mute" /> : null}
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                {item.hint ? <span className="shrink-0 font-mono text-2xs text-mute">{item.hint}</span> : null}
                {item.shortcut ? <Kbd>{item.shortcut}</Kbd> : null}
              </Command.Item>
            ))}
          </Command.Group>
        ))}
      </Command.List>
      <div className="flex items-center justify-between gap-4 border-t border-hair px-3 py-2 font-mono text-2xs uppercase tracking-[0.18em] text-mute">
        {footer ?? (
          <>
            <span className="flex items-center gap-1.5">
              <Kbd>↑</Kbd>
              <Kbd>↓</Kbd> navigate
            </span>
            <span className="flex items-center gap-1.5">
              <Kbd>↵</Kbd> open <span className="text-line">·</span> <Kbd>Esc</Kbd> close
            </span>
          </>
        )}
      </div>
    </Command.Dialog>
  );
}
